// File: src/components/admin/analysis-tasks/AnalysisTaskListView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// Admin list view for AI analysis task management

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Typography, Chip, IconButton, Switch, FormControlLabel,
    CircularProgress, Alert, Snackbar, Paper, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Sync as SyncIcon,
    PowerSettingsNew as PowerIcon,
    ContentCopy as ContentCopyIcon,
    AutoAwesome as AutoAwesomeIcon,
    Dashboard as DashboardIcon,
    Assignment as TasksIcon,
    CheckCircle as ActiveIcon,
    PlayCircle as ExecutionsIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';
import { StatCard } from '../scheduler/SchedulerStatsCards';

const STATUS_COLORS = {
    active: 'success',
    paused: 'warning',
    disabled: 'default',
};

const AnalysisTaskListView = () => {
    const navigate = useNavigate();

    // Data state
    const [tasks, setTasks] = useState([]);
    const [globalStats, setGlobalStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Master switch state
    const [masterEnabled, setMasterEnabled] = useState(true);
    const [masterSwitchLoading, setMasterSwitchLoading] = useState(false);

    // Re-sync schedules (Space-aware reconciliation) state
    const [resyncOpen, setResyncOpen] = useState(false);
    const [resyncLoading, setResyncLoading] = useState(false);
    const [resyncResult, setResyncResult] = useState(null);

    // Create dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', description: '' });
    const [createSaving, setCreateSaving] = useState(false);

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTaskId, setDeleteTaskId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Create choice dialog state (Issue #64)
    const [createChoiceOpen, setCreateChoiceOpen] = useState(false);

    // Fetch all data
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [tasksData, statsData] = await Promise.all([
                adminApi.getAnalysisTasks(),
                adminApi.getGlobalAnalysisStats().catch(() => null),
            ]);
            setTasks(tasksData.tasks || tasksData || []);
            if (statsData) {
                setGlobalStats(statsData);
                if (statsData.masterEnabled !== undefined) {
                    setMasterEnabled(statsData.masterEnabled);
                }
            }
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle master switch toggle
    const handleMasterSwitch = async (event) => {
        const enabled = event.target.checked;
        setMasterSwitchLoading(true);
        try {
            await adminApi.toggleMasterSwitch(enabled);
            setMasterEnabled(enabled);
            setSnackbar({
                open: true,
                message: enabled ? 'AI Analysis tasks enabled globally' : 'AI Analysis tasks disabled globally',
                severity: 'success',
            });
            fetchData();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to toggle master switch: ${message}`, severity: 'error' });
        } finally {
            setMasterSwitchLoading(false);
        }
    };

    // Re-sync ALL schedules: reconciles every active task so each user's
    // managed schedules are re-fanned per (member, Space). This is the repair
    // action that makes shared-Space users covered by background tasks.
    const handleResyncSchedules = async () => {
        setResyncLoading(true);
        setResyncResult(null);
        try {
            const result = await adminApi.resyncAllSchedules();
            setResyncResult(result);
            const errCount = result?.errors?.length || 0;
            setSnackbar({
                open: true,
                message: `Re-synced ${result?.tasksProcessed ?? 0} task(s): +${result?.totalAdded ?? 0} added, -${result?.totalRemoved ?? 0} removed, ~${result?.totalUpdated ?? 0} updated${errCount ? ` (${errCount} error(s))` : ''}.`,
                severity: errCount ? 'warning' : 'success',
            });
            fetchData();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setResyncResult({ error: message });
            setSnackbar({ open: true, message: `Failed to re-sync schedules: ${message}`, severity: 'error' });
        } finally {
            setResyncLoading(false);
        }
    };

    // Handle status toggle for individual task
    const handleStatusToggle = async (task) => {
        const newStatus = task.status === 'active' ? 'paused' : 'active';
        try {
            await adminApi.updateAnalysisTask(task.id, { status: newStatus });
            setSnackbar({
                open: true,
                message: `Task "${task.name}" ${newStatus === 'active' ? 'activated' : 'paused'}`,
                severity: 'success',
            });
            fetchData();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to update status: ${message}`, severity: 'error' });
        }
    };

    // Handle create task
    const handleCreateTask = async () => {
        setCreateSaving(true);
        try {
            const payload = {
                ...createForm,
                systemPrompt: 'You are an AI assistant that analyzes Google Ads campaign data and provides actionable insights.',
            };
            const result = await adminApi.createAnalysisTask(payload);
            setSnackbar({ open: true, message: 'Task created successfully', severity: 'success' });
            setCreateDialogOpen(false);
            setCreateForm({ name: '', description: '' });
            // Navigate to the new task detail view
            if (result.id || result.task?.id) {
                navigate(`/analysis-tasks/${result.id || result.task.id}/ai-config`);
            } else {
                fetchData();
            }
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to create task: ${message}`, severity: 'error' });
        } finally {
            setCreateSaving(false);
        }
    };

    // Handle delete confirmation
    const handleDeleteClick = (taskId) => {
        setDeleteTaskId(taskId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        try {
            await adminApi.deleteAnalysisTask(deleteTaskId);
            setSnackbar({ open: true, message: 'Task deleted successfully', severity: 'success' });
            setDeleteDialogOpen(false);
            setDeleteTaskId(null);
            fetchData();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to delete task: ${message}`, severity: 'error' });
        } finally {
            setDeleteLoading(false);
        }
    };

    // Handle clone task (Issue #64)
    const handleCloneTask = async (taskId) => {
        try {
            const cloned = await adminApi.cloneAnalysisTask(taskId);
            setSnackbar({ open: true, message: 'Task cloned successfully', severity: 'success' });
            const clonedId = cloned.id || cloned.task?.id;
            if (clonedId) {
                navigate(`/analysis-tasks/${clonedId}/ai-config`);
            } else {
                fetchData();
            }
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to clone task: ${message}`, severity: 'error' });
        }
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loading && tasks.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-testid="analysis-task-list-view">
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" fontWeight={600}>
                    AI Analysis Tasks
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Master Kill Switch */}
                    <Paper
                        data-testid="analysis-task-master-switch"
                        variant="outlined"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            py: 0.5,
                            borderColor: masterEnabled ? 'success.main' : 'error.main',
                            bgcolor: masterEnabled ? 'success.50' : 'error.50',
                        }}
                    >
                        <PowerIcon
                            sx={{ mr: 1, color: masterEnabled ? 'success.main' : 'error.main' }}
                            fontSize="small"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={masterEnabled}
                                    onChange={handleMasterSwitch}
                                    disabled={masterSwitchLoading}
                                    color="success"
                                    size="small"
                                    data-testid="analysis-task-master-switch-toggle"
                                />
                            }
                            label={
                                <Typography variant="body2" fontWeight={600}>
                                    {masterEnabled ? 'System Active' : 'System Disabled'}
                                </Typography>
                            }
                        />
                    </Paper>

                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchData}
                        disabled={loading}
                        data-testid="analysis-task-btn-refresh"
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<SyncIcon />}
                        onClick={() => { setResyncResult(null); setResyncOpen(true); }}
                        disabled={resyncLoading}
                        data-testid="analysis-task-btn-resync"
                    >
                        Re-sync Schedules
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateChoiceOpen(true)}
                        data-testid="analysis-task-btn-create"
                    >
                        Create Task
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error.message}
                </Alert>
            )}

            {/* Global Stats Summary */}
            {globalStats && (
                <Box data-testid="analysis-task-stats" sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <StatCard
                        title="Total Tasks"
                        value={globalStats.totalTasks ?? tasks.length}
                        icon={TasksIcon}
                        color="#1976d2"
                        loading={loading}
                    />
                    <StatCard
                        title="Active"
                        value={globalStats.activeTasks ?? tasks.filter(t => t.status === 'active').length}
                        icon={ActiveIcon}
                        color="#2e7d32"
                        loading={loading}
                    />
                    <StatCard
                        title="Executions (7d)"
                        value={globalStats.recentExecutions ?? '-'}
                        icon={ExecutionsIcon}
                        color="#9c27b0"
                        loading={loading}
                    />
                    <StatCard
                        title="Errors (7d)"
                        value={globalStats.recentErrors ?? '-'}
                        icon={ErrorIcon}
                        color={globalStats.recentErrors > 0 ? '#d32f2f' : '#757575'}
                        loading={loading}
                    />
                </Box>
            )}

            {/* Tasks Table */}
            <TableContainer component={Paper} data-testid="analysis-task-table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Data Access Tier</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tasks.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                        No analysis tasks configured yet. Click "Create Task" to get started.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {tasks.map((task) => (
                            <TableRow
                                key={task.id}
                                hover
                                onDoubleClick={() => navigate(`/analysis-tasks/${task.id}/ai-config`)}
                                sx={{ cursor: 'pointer' }}
                                data-testid={`analysis-task-row-${task.id}`}
                            >
                                <TableCell>
                                    <Typography fontWeight="bold" data-testid={`analysis-task-name-${task.id}`}>{task.name}</Typography>
                                    {task.description && (
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                                            {task.description}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={task.status || 'disabled'}
                                        color={STATUS_COLORS[task.status] || 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {task.dataAccessTier || 'content'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {task.createdAt ? new Date(task.createdAt).toLocaleDateString() : '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Switch
                                        checked={task.status === 'active'}
                                        onChange={() => handleStatusToggle(task)}
                                        size="small"
                                        color="success"
                                        title={task.status === 'active' ? 'Pause task' : 'Activate task'}
                                        data-testid={`analysis-task-status-toggle-${task.id}`}
                                    />
                                    <IconButton
                                        onClick={(e) => { e.stopPropagation(); navigate(`/analysis-tasks/${task.id}/ai-config`); }}
                                        size="small"
                                        color="primary"
                                        title="Edit task"
                                        data-testid={`analysis-task-btn-edit-${task.id}`}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={(e) => { e.stopPropagation(); handleCloneTask(task.id); }}
                                        size="small"
                                        color="info"
                                        title="Clone task"
                                        data-testid={`analysis-task-btn-clone-${task.id}`}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(task.id); }}
                                        size="small"
                                        color="error"
                                        title="Delete task"
                                        data-testid={`analysis-task-btn-delete-${task.id}`}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Choice Dialog (Issue #64) */}
            <Dialog open={createChoiceOpen} onClose={() => setCreateChoiceOpen(false)} maxWidth="sm" fullWidth data-testid="analysis-task-create-choice-dialog">
                <DialogTitle>Create New Analysis Task</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Choose how you'd like to create your task:
                    </DialogContentText>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            startIcon={<DashboardIcon />}
                            onClick={() => { setCreateChoiceOpen(false); navigate('/analysis-tasks/new/templates'); }}
                            sx={{ justifyContent: 'flex-start', py: 1.5, textTransform: 'none' }}
                            data-testid="analysis-task-create-from-template"
                        >
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography variant="subtitle2">Start from Template</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Browse curated templates for common use cases
                                </Typography>
                            </Box>
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => { setCreateChoiceOpen(false); navigate('/analysis-tasks/new/generate'); }}
                            sx={{ justifyContent: 'flex-start', py: 1.5, textTransform: 'none' }}
                            data-testid="analysis-task-create-ai-generate"
                        >
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography variant="subtitle2">Generate with AI</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Describe what you need and let AI build the config
                                </Typography>
                            </Box>
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => { setCreateChoiceOpen(false); setCreateDialogOpen(true); }}
                            sx={{ justifyContent: 'flex-start', py: 1.5, textTransform: 'none' }}
                            data-testid="analysis-task-create-from-scratch"
                        >
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography variant="subtitle2">Start from Scratch</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Manually configure all task settings
                                </Typography>
                            </Box>
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateChoiceOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Create Task Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth data-testid="analysis-task-create-dialog">
                <DialogTitle>Create New Analysis Task</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Task Name"
                            value={createForm.name}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                            required
                            data-testid="analysis-task-create-input-name"
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={createForm.description}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                            multiline
                            rows={2}
                            data-testid="analysis-task-create-input-description"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)} data-testid="analysis-task-create-btn-cancel">Cancel</Button>
                    <Button
                        onClick={handleCreateTask}
                        variant="contained"
                        disabled={createSaving || !createForm.name.trim()}
                        data-testid="analysis-task-create-btn-submit"
                    >
                        {createSaving ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} data-testid="analysis-task-delete-dialog">
                <DialogTitle>Delete Analysis Task</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this analysis task? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} data-testid="analysis-task-delete-btn-cancel">Cancel</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        data-testid="analysis-task-delete-btn-confirm"
                    >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Re-sync schedules (Space-aware reconciliation) dialog */}
            <Dialog open={resyncOpen} onClose={() => !resyncLoading && setResyncOpen(false)} maxWidth="sm" fullWidth data-testid="analysis-task-resync-dialog">
                <DialogTitle>Re-sync all schedules</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        Reconciles <strong>every active analysis task</strong> and re-fans each
                        user's managed schedules <strong>per Space</strong>. Use this to repair
                        existing schedules so users whose campaigns live in a shared Space are
                        covered by background tasks going forward.
                        <br /><br />
                        This scans all subscribers and may take up to a minute. It is safe to run
                        repeatedly.
                    </DialogContentText>

                    {resyncResult && !resyncResult.error && (
                        <Alert severity={resyncResult.errors?.length ? 'warning' : 'success'} sx={{ mt: 2 }} data-testid="analysis-task-resync-result">
                            Re-synced <strong>{resyncResult.tasksProcessed ?? 0}</strong> task(s):
                            {' '}<strong>+{resyncResult.totalAdded ?? 0}</strong> schedule(s) added,
                            {' '}<strong>-{resyncResult.totalRemoved ?? 0}</strong> removed,
                            {' '}<strong>~{resyncResult.totalUpdated ?? 0}</strong> updated
                            {resyncResult.errors?.length ? ` — ${resyncResult.errors.length} error(s)` : '.'}
                        </Alert>
                    )}
                    {resyncResult?.error && (
                        <Alert severity="error" sx={{ mt: 2 }}>{resyncResult.error}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResyncOpen(false)} disabled={resyncLoading} data-testid="analysis-task-resync-btn-close">
                        {resyncResult ? 'Close' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleResyncSchedules}
                        color="secondary"
                        variant="contained"
                        startIcon={resyncLoading ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                        disabled={resyncLoading}
                        data-testid="analysis-task-resync-btn-confirm"
                    >
                        {resyncLoading ? 'Re-syncing…' : (resyncResult ? 'Run again' : 'Re-sync now')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AnalysisTaskListView;
